import { AutosizeTextarea } from "@babackapp/ui/components/autosize-textarea";
import { useCallback, useRef, useState, useMemo, useEffect } from "react";
import { getAuthorName } from "@/pages/(app)/returns/utils/get-author-name.ts";
import type { ShopPersonUser } from "@/lib/people/use-shop-people";

type MentionedUser = {
  userId: string;
  displayName: string;
};

/** Members and Baback support (see useShopPeople). */
type MentionCandidate = {
  userId: string;
  user?: ShopPersonUser | null;
  /** Shown in the list, e.g. "First Last (Support Baback)". */
  displayName?: string;
};

type MentionTextareaProps = {
  value: string;
  onChange: (value: string) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  placeholder?: string;
  className?: string;
  members: MentionCandidate[];
  onMentionsChange: (mentionedUserIds: string[]) => void;
};

export default function MentionTextarea({
  value,
  onChange,
  onKeyDown,
  placeholder,
  className,
  members,
  onMentionsChange,
}: MentionTextareaProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [mentionedUsers, setMentionedUsers] = useState<MentionedUser[]>([]);
  const textareaRef = useRef<{ textArea: HTMLTextAreaElement } | null>(null);
  const mentionStartRef = useRef<number | null>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const filteredMembers = useMemo(() => {
    const seen = new Set<string>();
    return members.filter((member) => {
      if (seen.has(member.userId)) return false;
      seen.add(member.userId);
      if (!search) return true;
      const name = getAuthorName(member.user) ?? "";
      const email = member.user?.email ?? "";
      const lowerSearch = search.toLowerCase();
      return (
        name.toLowerCase().includes(lowerSearch) ||
        email.toLowerCase().includes(lowerSearch)
      );
    });
  }, [members, search]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newValue = e.target.value;
      const cursorPos = e.target.selectionStart;
      onChange(newValue);

      // Sync mentionedUsers: remove any whose @displayName is no longer in the text
      setMentionedUsers((prev) => {
        const remaining = prev.filter((m) =>
          newValue.includes(`@${m.displayName}`),
        );
        if (remaining.length !== prev.length) {
          onMentionsChange(remaining.map((m) => m.userId));
        }
        return remaining;
      });

      const textBeforeCursor = newValue.slice(0, cursorPos);
      const lastAtIndex = textBeforeCursor.lastIndexOf("@");

      if (lastAtIndex !== -1) {
        const textAfterAt = textBeforeCursor.slice(lastAtIndex + 1);
        const charBeforeAt = lastAtIndex > 0 ? newValue[lastAtIndex - 1] : " ";
        if (
          (charBeforeAt === " " ||
            charBeforeAt === "\n" ||
            lastAtIndex === 0) &&
          !textAfterAt.includes(" ")
        ) {
          mentionStartRef.current = lastAtIndex;
          setSearch(textAfterAt);
          setSelectedIndex(0);
          setIsOpen(true);
          return;
        }
      }

      setIsOpen(false);
      mentionStartRef.current = null;
    },
    [onChange, onMentionsChange],
  );

  const selectMember = useCallback(
    (member: MentionCandidate) => {
      const displayName =
        getAuthorName(member.user) ?? member.user?.email ?? "Utilisateur";
      const startPos = mentionStartRef.current;

      if (startPos === null) return;

      const textarea = textareaRef.current?.textArea;
      if (!textarea) return;

      const cursorPos = textarea.selectionStart;
      const before = value.slice(0, startPos);
      const after = value.slice(cursorPos);
      const newValue = `${before}@${displayName} ${after}`;

      onChange(newValue);

      const newMentions = [
        ...mentionedUsers.filter((m) => m.userId !== member.userId),
        { userId: member.userId, displayName },
      ];
      setMentionedUsers(newMentions);
      onMentionsChange(newMentions.map((m) => m.userId));

      setIsOpen(false);
      mentionStartRef.current = null;

      requestAnimationFrame(() => {
        const newCursorPos = startPos + displayName.length + 2;
        textarea.focus();
        textarea.setSelectionRange(newCursorPos, newCursorPos);
      });
    },
    [value, onChange, mentionedUsers, onMentionsChange],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (isOpen && filteredMembers.length > 0) {
        if (e.key === "ArrowDown") {
          e.preventDefault();
          setSelectedIndex((i) => (i < filteredMembers.length - 1 ? i + 1 : 0));
          return;
        }
        if (e.key === "ArrowUp") {
          e.preventDefault();
          setSelectedIndex((i) => (i > 0 ? i - 1 : filteredMembers.length - 1));
          return;
        }
        if (e.key === "Enter" || e.key === "Tab") {
          e.preventDefault();
          selectMember(filteredMembers[selectedIndex]);
          return;
        }
        if (e.key === "Escape") {
          e.preventDefault();
          setIsOpen(false);
          mentionStartRef.current = null;
          return;
        }
      }
      onKeyDown?.(e);
    },
    [isOpen, filteredMembers, selectedIndex, selectMember, onKeyDown],
  );

  const showDropdown = isOpen && filteredMembers.length > 0;

  const selectedItemRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!showDropdown) return;
    selectedItemRef.current?.scrollIntoView({ block: "nearest" });
  }, [selectedIndex, showDropdown]);

  return (
    <div className="relative">
      <AutosizeTextarea
        ref={textareaRef}
        className={className}
        placeholder={placeholder}
        onChange={handleChange}
        value={value}
        onKeyDown={handleKeyDown}
        onBlur={() => {
          setTimeout(() => setIsOpen(false), 200);
        }}
      />
      {showDropdown && (
        <div className="absolute bottom-full left-0 mb-1 z-50 w-64 rounded-md border bg-popover text-popover-foreground shadow-md overflow-hidden">
          <div ref={listRef} className="max-h-[200px] overflow-y-auto p-1">
            {filteredMembers.map((member, index) => {
              const name =
                member.displayName ??
                getAuthorName(member.user) ??
                member.user?.email ??
                "Utilisateur";
              return (
                <button
                  key={member.userId}
                  ref={index === selectedIndex ? selectedItemRef : undefined}
                  type="button"
                  className={`grid grid-cols-[20px_1fr] w-full gap-2 items-center rounded-sm px-2 py-1.5 text-sm cursor-pointer text-left ${
                    index === selectedIndex
                      ? "bg-accent text-accent-foreground"
                      : "hover:bg-accent/50"
                  }`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    selectMember(member);
                  }}
                  onMouseEnter={() => setSelectedIndex(index)}
                >
                  <div className="flex items-center justify-center bg-primary w-5 h-5 rounded-full">
                    <span className="text-[10px] text-primary-foreground">
                      {name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="overflow-hidden">
                    <div className="text-sm truncate">{name}</div>
                    {member.user?.email && name !== member.user.email && (
                      <div className="text-xs text-muted-foreground truncate">
                        {member.user.email}
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
